import React, { useCallback } from 'react';

import localizedStrings from 'localization';
import {
  PublishedShowcaseContainer,
  PublishedShowcaseTitleAndSubtitle,
  PublishedShowcaseTitle,
  PublishedShowcaseSubtitle,
  PublishedShowcaseShareAndView,
  PublishedShowcaseTextButton,
  PublishedShowcaseSeparator,
} from 'modules/profile/components';
import { ShowcaseItem } from 'modules/profile/typings';

type PublishedShowcaseItemProps = {
  showcase: ShowcaseItem;
  index: number;
  maxIndex: number;
  onEditShowcaseClick?: (showcaseId: string) => void;
  onDeleteShowcaseClick?: (showcaseId: string) => void;
  onShareShowcaseClick?: (showcaseId: string) => void;
  onViewShowcaseClick: (showcaseId: string) => void;
  showShareAndDelete?: boolean;
};

export const INITIAL_VERSION_READ_ONLY = '1.0';

const PublishedShowcaseItem = ({
  showcase,
  index,
  maxIndex,
  onEditShowcaseClick = () => { },
  onDeleteShowcaseClick = () => { },
  onShareShowcaseClick = () => { },
  onViewShowcaseClick,
  showShareAndDelete = true
}: PublishedShowcaseItemProps) => {
  const createdAtDate = new Date(showcase.createdAt);
  const enableEdition = showcase?.showcaseVersionName && showcase?.showcaseVersionName !== INITIAL_VERSION_READ_ONLY;

  const handleEditShowcase = useCallback(() => {
    onEditShowcaseClick(showcase.id);
  }, [onEditShowcaseClick, showcase]);

  const handleDeleteShowcase = useCallback(() => {
    onDeleteShowcaseClick(showcase.id);
  }, [onDeleteShowcaseClick, showcase]);

  const handleShareShowcase = useCallback(() => {
    onShareShowcaseClick(showcase.id);
  }, [onShareShowcaseClick, showcase]);

  const handleViewShowcase = useCallback(() => {
    onViewShowcaseClick(showcase.id);
  }, [onViewShowcaseClick, showcase.id]);

  return (
    <>
      <PublishedShowcaseContainer>
        <PublishedShowcaseTitleAndSubtitle>
          <PublishedShowcaseTitle>{showcase.title}</PublishedShowcaseTitle>
          <PublishedShowcaseSubtitle>
            {localizedStrings.publishedOn}
            {createdAtDate.getUTCMonth() + 1}/{createdAtDate.getDate()}/
            {createdAtDate.getFullYear()}
          </PublishedShowcaseSubtitle>
        </PublishedShowcaseTitleAndSubtitle>
        <PublishedShowcaseShareAndView>
          {showShareAndDelete && (
            <>
              {enableEdition ? <>
                <PublishedShowcaseTextButton data-testid="PublishedShowcaseTextButton_edit" onClick={handleEditShowcase}>
                  {localizedStrings.edit}
                </PublishedShowcaseTextButton>
              </> : null}
              <PublishedShowcaseTextButton data-testid="PublishedShowcaseTextButton_delete" onClick={handleDeleteShowcase}>
                {localizedStrings.delete}
              </PublishedShowcaseTextButton>
              <PublishedShowcaseTextButton data-testid="PublishedShowcaseTextButton_share" onClick={handleShareShowcase}>
                {localizedStrings.share}
              </PublishedShowcaseTextButton>
            </>
          )}
          <PublishedShowcaseTextButton onClick={handleViewShowcase}>
            {localizedStrings.viewShowcase}
          </PublishedShowcaseTextButton>
        </PublishedShowcaseShareAndView>
      </PublishedShowcaseContainer>
      {index !== maxIndex && <PublishedShowcaseSeparator />}
    </>
  );
};

export default PublishedShowcaseItem;
